import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@/components/icons';
import { useDismiss } from '@/hooks/useDismiss';
import { Button } from './Button';
import styles from '@/styles/components/Modal/style.scss';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  width?: number;
}

/**
 * Centered dialog with header, scrolling body and footer. Closes on Escape and the × button.
 * On open it focuses the first element marked `data-autofocus` (use that, not `autoFocus`), else the dialog;
 * on close focus returns to where it was.
 */
export function Modal({ open, onClose, title, subtitle, footer, children, width = 620 }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDismiss(open, onClose);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    (dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]') ?? dialogRef.current)?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className={styles.backdrop}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        className={styles.dialog}
        style={{ maxWidth: width }}
      >
        <header className={styles.header}>
          <div className={styles.titles}>
            <h2 id="modal-title" className={styles.title}>
              {title}
            </h2>
            {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
          </div>
          <Button iconOnly variant="ghost" size="md" aria-label="Close" icon={<CloseIcon size={16} />} onClick={onClose} className={styles.close} />
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
